import React, { forwardRef, Ref, useCallback, useRef, useState } from "react";

import { StyledDiv, StyledInput, StyledSpan } from "./Styles";
import { useForkRef } from "../../utils";
import { Variant } from "../Typography/Typography";

export interface EditableContentInterface {
  /** Typography variant. */
  variant?: Variant;
  /** Typography weight. */
  weight?: "light" | "regular" | "bold" | "medium";
  /** Input props. */
  inputProps?: Partial<React.ComponentPropsWithoutRef<"input">>;
  /** Disable edit mode. */
  disabled?: boolean;
  invalid?: boolean;
  /** Wrapper reference. */
  wrapRef?: Ref<HTMLDivElement>;
}

export const EditableContent = forwardRef<
  HTMLDivElement,
  EditableContentInterface &
    Omit<React.ComponentPropsWithoutRef<"div">, keyof EditableContentInterface>
>(
  (
    {
      className,
      variant = "Body 2",
      weight,
      disabled = false,
      inputProps,
      wrapRef,
      children,
      invalid,
      onMouseLeave,
      ...props
    },
    ref
  ) => {
    const intInputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef(ref, intInputRef);
    const [suppressHover, setSuppressHover] = useState(false);

    const handleBlur = useCallback(
      (evt) => {
        if (intInputRef?.current) {
          intInputRef.current.scrollLeft = 0;
        }
        typeof inputProps?.onBlur === `function` && inputProps?.onBlur(evt);
      },
      [inputProps]
    );

    const handleKeyDown = useCallback(
      (evt) => {
        if (evt.key === "Enter" || evt.key === "Escape") {
          setSuppressHover(true);
        }
        typeof inputProps?.onKeyDown === "function" &&
          inputProps.onKeyDown(evt);
      },
      [inputProps]
    );

    const handleFocus = useCallback(
      (evt) => {
        setSuppressHover(false);
        typeof inputProps?.onFocus === "function" && inputProps.onFocus(evt);
      },
      [inputProps]
    );

    const handleMouseLeave = useCallback(
      (evt) => {
        setSuppressHover(false);
        typeof onMouseLeave === "function" && onMouseLeave(evt);
      },
      [onMouseLeave]
    );

    return (
      <StyledDiv
        className={className}
        ref={wrapRef}
        {...props}
        onMouseLeave={handleMouseLeave}
      >
        <StyledSpan
          variant={variant}
          forwardedAs="span"
          weight={weight}
          $disabled={disabled}
          className="presentation"
        >
          {children ? children : inputProps?.value}
        </StyledSpan>
        <StyledInput
          {...inputProps}
          ref={handleRef}
          forwardedAs="input"
          variant={variant}
          weight={weight}
          value={inputProps?.value}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          disabled={disabled}
          data-form-type="other"
          data-hover-suppressed={suppressHover ? "" : undefined}
          invalid={invalid}
        />
      </StyledDiv>
    );
  }
);

EditableContent.displayName = "EditableContent";
